import React, { useState, useEffect, useMemo, useRef } from "react";
import { Icon } from "@iconify/react";
import { Link } from "react-router-dom";
import { exportToGoogleSheets } from "../utils/exportToGoogleSheets";

// Google Business column registry definitions
const ALL_COLUMNS = [
  // Identity & Core
  { key: "title", label: "Location Title", category: "General", width: 220, defaultVisible: true },
  { key: "store_code", label: "Store Code", category: "General", width: 150, defaultVisible: true },
  { key: "primary_category_name", label: "Category", category: "General", width: 180, defaultVisible: true },
  { key: "open_status", label: "Open Status", category: "General", width: 140, defaultVisible: true, type: "badge" },
  { key: "is_verified", label: "Confirmed", category: "General", width: 145, defaultVisible: true, type: "badge" },
  { key: "description", label: "Description", category: "General", width: 250, defaultVisible: false },
  
  // Address & Geo
  { key: "address_line1", label: "Address Line 1", category: "Address", width: 200, defaultVisible: true },
  { key: "address_line2", label: "Address Line 2", category: "Address", width: 150, defaultVisible: false },
  { key: "postal_code", label: "Postal Code", category: "Address", width: 120, defaultVisible: true },
  { key: "city", label: "City", category: "Address", width: 150, defaultVisible: true },
  { key: "state", label: "State / Region", category: "Address", width: 140, defaultVisible: true },
  { key: "country_code", label: "Country Code", category: "Address", width: 120, defaultVisible: true },
  { key: "latitude", label: "Latitude", category: "Address", width: 130, defaultVisible: true },
  { key: "longitude", label: "Longitude", category: "Address", width: 130, defaultVisible: true },

  // Contact & Links
  { key: "primary_phone", label: "Phone", category: "Contact", width: 160, defaultVisible: true },
  { key: "website_uri", label: "Website", category: "Contact", width: 200, defaultVisible: true, type: "link" },
  { key: "maps_uri", label: "Maps URL", category: "Contact", width: 160, defaultVisible: false, type: "link" },
  { key: "google_location_id", label: "Google Location ID", category: "System", width: 220, defaultVisible: false },

  // Metadata
  { key: "version_number", label: "Ver #", category: "System", width: 90, defaultVisible: true, type: "version" },
  { key: "updated_at", label: "Last Modified", category: "System", width: 160, defaultVisible: true, type: "datetime" },
];

const STORAGE_KEY_COLUMNS = "loqation_google_business_sheet_columns_v2";

const GoogleBusinessSpreadsheetView = ({
  data = [],
  loading = false,
  onRefresh,
  onOpenHistory,
}) => {
  // 1. Column Config & Ordering
  const [columnsOrder, setColumnsOrder] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_COLUMNS);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const savedKeys = new Set(parsed.map((c) => c.key));
          const missing = ALL_COLUMNS.filter((c) => !savedKeys.has(c.key)).map((c) => ({
            key: c.key,
            visible: c.defaultVisible,
          }));
          return [...parsed, ...missing];
        }
      }
    } catch (e) {
      console.error("Failed to parse column preferences:", e);
    }
    return ALL_COLUMNS.map((c) => ({ key: c.key, visible: c.defaultVisible }));
  });

  const [columnSearch, setColumnSearch] = useState("");
  const [isColumnDropdownOpen, setIsColumnDropdownOpen] = useState(false);
  const [activeFilterText, setActiveFilterText] = useState("");

  const dropdownRef = useRef(null);

  // Save column order to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_COLUMNS, JSON.stringify(columnsOrder));
    } catch (e) {
      console.error("Failed to store column preferences:", e);
    }
  }, [columnsOrder]);

  // Close column dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsColumnDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Map of column details
  const colMap = useMemo(() => {
    const m = new Map();
    ALL_COLUMNS.forEach((c) => m.set(c.key, c));
    return m;
  }, []);

  // Visible columns in user's custom sequence
  const visibleColumns = useMemo(() => {
    return columnsOrder
      .filter((c) => c.visible)
      .map((c) => colMap.get(c.key))
      .filter(Boolean);
  }, [columnsOrder, colMap]);

  // Column Reordering: Move column Up or Down in sequence
  const handleMoveColumn = (index, direction, e) => {
    e.stopPropagation();
    const newOrder = [...columnsOrder];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newOrder.length) return;

    const [moved] = newOrder.splice(index, 1);
    newOrder.splice(targetIndex, 0, moved);
    setColumnsOrder(newOrder);
  };

  // Toggle single column visibility
  const handleToggleColumn = (key) => {
    setColumnsOrder((prev) =>
      prev.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c))
    );
  };

  // Select all columns
  const handleSelectAllColumns = (visible) => {
    setColumnsOrder((prev) => prev.map((c) => ({ ...c, visible })));
  };

  // Reset columns to default
  const handleResetColumns = () => {
    setColumnsOrder(ALL_COLUMNS.map((c) => ({ key: c.key, visible: c.defaultVisible })));
  };

  // Filtered rows
  const filteredData = useMemo(() => {
    if (!activeFilterText.trim()) return data;
    const term = activeFilterText.toLowerCase();
    return data.filter((row) => {
      return (
        String(row.title || "").toLowerCase().includes(term) ||
        String(row.store_code || "").toLowerCase().includes(term) ||
        String(row.city || "").toLowerCase().includes(term) ||
        String(row.address_line1 || "").toLowerCase().includes(term) ||
        String(row.primary_category_name || "").toLowerCase().includes(term) ||
        String(row.primary_phone || "").toLowerCase().includes(term)
      );
    });
  }, [data, activeFilterText]);

  // CSV Export
  const handleExportCSV = () => {
    if (filteredData.length === 0) return;

    const headers = visibleColumns.map((c) => `"${c.label.replace(/"/g, '""')}"`);
    const rows = filteredData.map((row) =>
      visibleColumns
        .map((col) => {
          let val = row[col.key];
          if (val == null) val = "";
          return `"${String(val).replace(/"/g, '""')}"`;
        })
        .join(",")
    );

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `google_business_locations_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const [exportingSheets, setExportingSheets] = useState(false);

  const handleExportGoogleSheets = async () => {
    if (filteredData.length === 0) return;

    const headers = visibleColumns.map((c) => c.label);
    const rows = filteredData.map((row) =>
      visibleColumns.map((col) => {
        const val = row[col.key];
        return val == null ? "" : String(val);
      })
    );

    setExportingSheets(true);
    try {
      await exportToGoogleSheets({
        title: `Google Business Locations (${new Date().toISOString().slice(0, 10)})`,
        headers,
        rows,
      });
    } finally {
      setExportingSheets(false);
    }
  };

  // Columns for dropdown filtered by search query
  const filteredColumnsForMenu = useMemo(() => {
    if (!columnSearch.trim()) return columnsOrder;
    const q = columnSearch.toLowerCase();
    return columnsOrder.filter((c) => {
      const def = colMap.get(c.key);
      return (
        (def?.label || "").toLowerCase().includes(q) ||
        (def?.category || "").toLowerCase().includes(q)
      );
    });
  }, [columnsOrder, columnSearch, colMap]);

  return (
    <div className="card border-0 shadow-sm radius-12 overflow-hidden mb-24">
      {/* Spreadsheet Control Bar */}
      <div className="card-header bg-white border-bottom py-16 px-20 d-flex flex-wrap align-items-center justify-content-between gap-3">
        {/* Left: Quick search filter & status */}
        <div className="d-flex align-items-center flex-wrap gap-2">
          <div className="position-relative" style={{ width: "240px" }}>
            <input
              type="text"
              className="form-control form-control-sm radius-8 ps-32 pe-12 bg-neutral-50 text-xs"
              style={{ paddingLeft: "34px", paddingRight: activeFilterText ? "28px" : "12px" }}
              placeholder="Search in sheet..."
              value={activeFilterText}
              onChange={(e) => setActiveFilterText(e.target.value)}
            />
            <Icon
              icon="solar:magnifer-linear"
              className="position-absolute start-0 top-50 translate-middle-y ms-10 text-secondary-light"
              style={{ fontSize: "15px" }}
            />
            {activeFilterText && (
              <button
                type="button"
                className="btn btn-link p-0 position-absolute end-0 top-50 translate-middle-y me-8 text-secondary-light"
                onClick={() => setActiveFilterText("")}
              >
                <Icon icon="mingcute:close-line" style={{ fontSize: "14px" }} />
              </button>
            )}
          </div>

          <span className="badge bg-light text-secondary border px-8 py-6 text-2xs fw-medium radius-6">
            <Icon icon="solar:database-outline" className="me-1" />
            {filteredData.length} records (Read-Only)
          </span>
        </div>

        {/* Right: Actions & Tools */}
        <div className="d-flex align-items-center flex-wrap gap-2">
          {/* Column Chooser Dropdown */}
          <div className="position-relative" ref={dropdownRef}>
            <button
              type="button"
              className={`btn btn-sm radius-8 d-flex align-items-center gap-2 ${
                isColumnDropdownOpen ? "btn-primary" : "btn-outline-neutral-300 text-neutral-700 bg-white"
              }`}
              onClick={() => setIsColumnDropdownOpen((prev) => !prev)}
            >
              <Icon icon="solar:settings-minimalistic-linear" className="text-base" />
              <span>Columns ({visibleColumns.length}/{ALL_COLUMNS.length})</span>
              <Icon icon={isColumnDropdownOpen ? "solar:alt-arrow-up-linear" : "solar:alt-arrow-down-linear"} className="text-xs" />
            </button>

            {/* Dropdown Menu */}
            {isColumnDropdownOpen && (
              <div
                className="dropdown-menu show shadow-lg border-0 radius-12 p-16 position-absolute end-0 mt-2 bg-white"
                style={{ width: "360px", maxHeight: "480px", zIndex: 1050 }}
              >
                <div className="d-flex align-items-center justify-content-between mb-12">
                  <h6 className="text-sm fw-bold mb-0 text-neutral-800">Select & Order Columns</h6>
                  <button
                    type="button"
                    className="btn btn-link p-0 text-xs text-primary-600 text-decoration-none"
                    onClick={handleResetColumns}
                  >
                    Reset Order
                  </button>
                </div>

                {/* Column Search */}
                <div className="mb-12">
                  <input
                    type="text"
                    className="form-control form-control-sm text-xs radius-8"
                    placeholder="Search columns..."
                    value={columnSearch}
                    onChange={(e) => setColumnSearch(e.target.value)}
                    autoFocus
                  />
                </div>

                {/* Bulk Actions */}
                <div className="d-flex align-items-center justify-content-between mb-8 pb-8 border-bottom">
                  <button
                    type="button"
                    className="btn btn-xs btn-link text-primary-600 text-decoration-none p-0"
                    onClick={() => handleSelectAllColumns(true)}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="btn btn-xs btn-link text-secondary-light text-decoration-none p-0"
                    onClick={() => handleSelectAllColumns(false)}
                  >
                    Hide All
                  </button>
                </div>

                {/* Column List with Up/Down and Checkbox */}
                <div style={{ maxHeight: "260px", overflowY: "auto" }} className="pe-1">
                  {filteredColumnsForMenu.map((c, index) => {
                    const colDef = colMap.get(c.key);
                    if (!colDef) return null;

                    const isFirst = index === 0;
                    const isLast = index === filteredColumnsForMenu.length - 1;

                    return (
                      <div
                        key={c.key}
                        className={`d-flex align-items-center justify-content-between py-6 px-8 radius-6 mb-2 ${
                          c.visible ? "bg-neutral-50" : "opacity-75"
                        }`}
                      >
                        <div className="form-check d-flex align-items-center gap-2 mb-0">
                          <input
                            className="form-check-input mt-0"
                            type="checkbox"
                            id={`col-chk-${c.key}`}
                            checked={c.visible}
                            onChange={() => handleToggleColumn(c.key)}
                          />
                          <label
                            className="form-check-label text-xs fw-medium text-neutral-800 cursor-pointer user-select-none"
                            htmlFor={`col-chk-${c.key}`}
                          >
                            {colDef.label}
                          </label>
                        </div>

                        {/* Order adjustment buttons */}
                        <div className="d-flex align-items-center gap-1">
                          <span className="badge bg-light text-secondary-light text-2xs me-1">
                            {colDef.category}
                          </span>
                          <button
                            type="button"
                            className="btn btn-xs btn-light p-1 radius-4"
                            disabled={isFirst}
                            onClick={(e) => handleMoveColumn(index, "up", e)}
                            title="Move Up"
                          >
                            <Icon icon="mingcute:arrow-up-line" style={{ fontSize: "12px" }} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-xs btn-light p-1 radius-4"
                            disabled={isLast}
                            onClick={(e) => handleMoveColumn(index, "down", e)}
                            title="Move Down"
                          >
                            <Icon icon="mingcute:arrow-down-line" style={{ fontSize: "12px" }} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Refresh Data */}
          {onRefresh && (
            <button
              type="button"
              className="btn btn-sm btn-outline-neutral-300 text-neutral-700 bg-white radius-8 d-flex align-items-center gap-2"
              onClick={onRefresh}
              title="Refresh Data"
            >
              <Icon icon="solar:refresh-linear" className={loading ? "spin" : "text-base"} />
            </button>
          )}

          {/* Export CSV */}
          <button
            type="button"
            className="btn btn-sm btn-outline-neutral-300 text-neutral-700 bg-white radius-8 d-flex align-items-center gap-2"
            onClick={handleExportCSV}
            title="Export Visible Data as CSV"
          >
            <Icon icon="solar:file-download-linear" className="text-base" />
            <span>CSV</span>
          </button>

          {/* Export Google Sheets */}
          <button
            type="button"
            className="btn btn-sm btn-outline-success radius-8 d-flex align-items-center gap-2 fw-semibold"
            onClick={handleExportGoogleSheets}
            disabled={exportingSheets}
            title="Export Visible Data to Google Sheets"
          >
            {exportingSheets ? (
              <Icon icon="line-md:loading-twotone-loop" className="text-base" />
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8 13H16M8 17H16M11 9V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
            <span>Google Sheets</span>
          </button>
        </div>
      </div>

      {/* Spreadsheet Main Grid Area */}
      <div className="card-body p-0 position-relative">
        {loading && (
          <div
            className="position-absolute w-100 h-100 bg-white bg-opacity-75 d-flex align-items-center justify-content-center"
            style={{ zIndex: 20 }}
          >
            <div className="d-flex flex-column align-items-center gap-2">
              <Icon icon="line-md:loading-twotone-loop" className="text-3xl text-primary-600" />
              <span className="text-xs fw-semibold text-secondary-light">Loading Google Business profiles...</span>
            </div>
          </div>
        )}

        <div className="table-responsive" style={{ maxHeight: "75vh", overflowX: "auto", overflowY: "auto" }}>
          <table className="table table-bordered table-hover align-middle mb-0 text-sm" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
            {/* Header */}
            <thead className="bg-neutral-100 text-secondary sticky-top" style={{ zIndex: 10 }}>
              <tr>
                {/* Fixed Index Column */}
                <th
                  className="text-center py-12 px-8 bg-neutral-100 border-bottom border-end position-sticky start-0 text-xs fw-bold"
                  style={{ width: "50px", minWidth: "50px", zIndex: 12 }}
                >
                  #
                </th>

                {/* Quick Action Column */}
                <th
                  className="text-center py-12 px-8 bg-neutral-100 border-bottom border-end position-sticky text-xs fw-bold"
                  style={{ left: "50px", width: "100px", minWidth: "100px", zIndex: 12 }}
                >
                  Actions
                </th>

                {/* Dynamic Visible Columns */}
                {visibleColumns.map((col) => (
                  <th
                    key={col.key}
                    className="py-12 px-12 border-bottom border-end text-xs fw-bold text-neutral-800 text-nowrap user-select-none"
                    style={{ width: `${col.width}px`, minWidth: `${col.width}px` }}
                  >
                    <div className="d-flex align-items-center justify-content-between gap-1">
                      <span>{col.label}</span>
                      <span className="text-2xs text-secondary-light fw-normal">
                        {col.type === "datetime" ? "🕒" : col.type === "version" ? "v#" : ""}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length + 2} className="text-center py-40 text-secondary-light">
                    {loading ? "Loading records..." : "No Google Business profiles found."}
                  </td>
                </tr>
              ) : (
                filteredData.map((row, idx) => {
                  return (
                    <tr key={row.id || idx}>
                      {/* Fixed Index */}
                      <td
                        className="text-center py-8 px-6 bg-neutral-50 border-end border-bottom position-sticky start-0 text-xs text-secondary-light fw-semibold"
                        style={{ zIndex: 5 }}
                      >
                        {idx + 1}
                      </td>

                      {/* Quick Action Icons */}
                      <td
                        className="text-center py-8 px-6 bg-neutral-50 border-end border-bottom position-sticky"
                        style={{ left: "50px", zIndex: 5 }}
                      >
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          {/* View Profile Details Link */}
                          <Link
                            to={`/google-business/${row.id}/view`}
                            className="btn btn-xs btn-outline-primary p-2 radius-4"
                            title="View Profile Details"
                          >
                            <Icon icon="solar:eye-linear" style={{ fontSize: "14px" }} />
                          </Link>

                          {/* Version History Modal */}
                          {onOpenHistory && (
                            <button
                              type="button"
                              className="btn btn-xs btn-outline-info p-2 radius-4"
                              onClick={() => onOpenHistory(row)}
                              title="Audit Version History"
                            >
                              <Icon icon="solar:history-linear" style={{ fontSize: "14px" }} />
                            </button>
                          )}

                          {/* Open Maps Link */}
                          {row.maps_uri && (
                            <a
                              href={row.maps_uri}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-xs btn-outline-secondary p-2 radius-4"
                              title="Open in Google Maps"
                            >
                              <Icon icon="solar:map-arrow-square-linear" style={{ fontSize: "14px" }} />
                            </a>
                          )}
                        </div>
                      </td>

                      {/* Read-Only Cell Presentation */}
                      {visibleColumns.map((col) => {
                        const cellVal = row[col.key];

                        return (
                          <td
                            key={col.key}
                            className="py-8 px-12 border-end border-bottom text-xs text-neutral-800"
                            style={{ width: `${col.width}px`, minWidth: `${col.width}px` }}
                          >
                            {col.type === "link" ? (
                              cellVal ? (
                                <a
                                  href={cellVal}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-primary-600 text-decoration-none d-inline-flex align-items-center gap-1 text-truncate"
                                  style={{ maxWidth: "200px" }}
                                  title={cellVal}
                                >
                                  <span>{cellVal.replace(/^https?:\/\//, "")}</span>
                                  <Icon icon="solar:arrow-right-up-linear" style={{ fontSize: "11px" }} />
                                </a>
                              ) : (
                                <span className="text-secondary-light">—</span>
                              )
                            ) : col.type === "badge" ? (
                              col.key === "is_verified" ? (
                                (() => {
                                  const upper = String(cellVal || "").toUpperCase();
                                  const isConf =
                                    row.is_confirmed === true ||
                                    upper === "VERIFIED" ||
                                    upper === "CONFIRMED" ||
                                    upper === "TRUE" ||
                                    upper === "1";
                                  const isPend = row.confirmation_status === "PENDING" || upper === "PENDING";
                                  return (
                                    <span
                                      className={`badge text-2xs px-8 py-4 radius-6 d-inline-flex align-items-center gap-1 ${
                                        isConf
                                          ? "bg-success-focus text-success-main"
                                          : isPend
                                            ? "bg-warning-focus text-warning-main"
                                            : "bg-danger-focus text-danger-main"
                                      }`}
                                    >
                                      <Icon
                                        icon={
                                          isConf
                                            ? "solar:verified-check-bold"
                                            : isPend
                                              ? "solar:clock-circle-bold"
                                              : "solar:close-circle-bold"
                                        }
                                        style={{ fontSize: "12px" }}
                                      />
                                      <span>{isConf ? "Confirmed" : isPend ? "Pending" : "Not Confirmed"}</span>
                                    </span>
                                  );
                                })()
                              ) : cellVal ? (
                                <span
                                  className={`badge text-2xs px-8 py-4 radius-6 ${
                                    cellVal === "OPEN" || cellVal === "VERIFIED"
                                      ? "bg-success-focus text-success-main"
                                      : cellVal === "CLOSED_PERMANENTLY"
                                      ? "bg-danger-focus text-danger-main"
                                      : "bg-warning-focus text-warning-main"
                                  }`}
                                >
                                  {cellVal}
                                </span>
                              ) : (
                                <span className="text-secondary-light">—</span>
                              )
                            ) : col.type === "datetime" ? (
                              <span className="text-secondary-light text-nowrap">
                                {cellVal ? new Date(cellVal).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" }) : "—"}
                              </span>
                            ) : col.type === "version" ? (
                              <span className="badge bg-neutral-100 text-neutral-700 text-2xs px-8 py-4 radius-6">
                                v{cellVal || 1}
                              </span>
                            ) : (
                              <div className="text-truncate" title={String(cellVal || "")} style={{ maxWidth: `${col.width - 24}px` }}>
                                {cellVal != null && cellVal !== "" ? String(cellVal) : <span className="text-secondary-light">—</span>}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default GoogleBusinessSpreadsheetView;
